// Investment mix (F7 / BAU / Stability) — pure-logic library for Phase 1b-3.
//
// Reads M4 (M4_ratio_actual) from each sprint's headline_metrics dict. The
// upstream envelope's `value` is a 3-element share array [F7, BAU, Stability]
// summing to ~1.0. Programme target is 60/30/10. All series outputs are
// oldest→newest.

import type { MetricEnvelope, SprintListItem } from "@/api/types";

const DEFAULT_WINDOW = 8;
export const PROGRAMME_TARGET: MixShares = { f7: 0.60, bau: 0.30, stability: 0.10 };
const DEFAULT_TARGET_TOLERANCE = 0.10; // ±10pp on the F7 axis

export interface MixShares {
  f7: number;
  bau: number;
  stability: number;
}

export interface MixPoint extends MixShares {
  sprintId: number;
  sprintName: string;
}

export type TargetVerdict = "on" | "off" | "unknown";

export function extractMixShares(env: MetricEnvelope | undefined): MixShares | null {
  if (!env) return null;
  const v = env.value;
  if (!Array.isArray(v) || v.length < 3) return null;
  const [f7, bau, stability] = v;
  if (typeof f7 !== "number" || typeof bau !== "number" || typeof stability !== "number") {
    return null;
  }
  return { f7, bau, stability };
}

/**
 * Last `n` closed sprints' investment mix, oldest→newest. Sprints without an
 * extractable M4 envelope are dropped — they can't be charted.
 */
export function mixSeries(
  sprints: SprintListItem[],
  n: number = DEFAULT_WINDOW,
): MixPoint[] {
  const closed = sprints.filter((s) => s.sprint_state === "closed");
  const window = closed.slice(0, n).reverse();
  const points: MixPoint[] = [];
  for (const s of window) {
    const shares = extractMixShares(s.headline_metrics?.M4);
    if (!shares) continue;
    points.push({ sprintId: s.sprint_id, sprintName: s.sprint_name, ...shares });
  }
  return points;
}

/**
 * On/off-target verdict for a single sprint's mix. The decision is driven
 * by the F7 axis: if F7 share is within `tolerance` (in absolute share-points)
 * of the target, the sprint is "on". F7 is the primary investment-balance
 * lever; BAU and Stability can drift around it.
 */
export function mixOnTarget(
  shares: MixShares | null,
  target: MixShares = PROGRAMME_TARGET,
  tolerance: number = DEFAULT_TARGET_TOLERANCE,
): TargetVerdict {
  if (!shares) return "unknown";
  return Math.abs(shares.f7 - target.f7) <= tolerance ? "on" : "off";
}
